import React, { useEffect, useRef } from "react";
import {
  Alert, Box, Button, Chip, Drawer, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Add as AddIcon, Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon, Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import {
  ListPageShell, ListToolbar, ListView, ListSkeleton, ListPagination,
} from "../../../components/organisms/list";
import EmptyState from "../../../components/atoms/EmptyState";
import { DrawerHeader, DrawerFooter, FormTextField } from "../../../components/atoms";
import { DrawerField, tileInputProps } from "../../../components/molecules";
import { FormDialog } from "../../../components/organisms";
import useAutoPageSize from "../../../hooks/useAutoPageSize";
import { useClinicTheme } from "../../../theme";
import { COLORS, TYPO } from "../../../styles/uiConstants";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { readExcelFile } from "./excelUtils";

/**
 * Mobil (md altı) kart gösterimi: ilk kolonun değeri başlık, kalan kolonlar
 * alt satırlar, sağda Düzenle + durum değiştir ikon butonları.
 */
function SystemVariableCard({ item, tableColumns, entityLabel, vm }) {
  const [titleCol, ...restCols] = tableColumns;
  const titleValue = titleCol?.render ? titleCol.render(item) : item?.[titleCol?.key] ?? "-";

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${COLORS.slate200}`,
        bgcolor: COLORS.white,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: COLORS.slate800 }} noWrap>
          {titleValue}
        </Typography>
        {restCols.map((col) => (
          <Typography key={col.key} sx={{ fontSize: "0.75rem", color: COLORS.slate500, mt: 0.25 }} noWrap>
            {col.render ? col.render(item) : item?.[col.key] ?? "-"}
          </Typography>
        ))}
      </Box>

      <Box sx={{ display: "flex", gap: 0.5, alignSelf: "flex-start", flexShrink: 0 }}>
        <IconButton size="small" aria-label={`${entityLabel} düzenle`} onClick={() => vm.openEdit(item)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`${entityLabel} sil`}
          onClick={() => vm.openDeleteConfirm(item)}
          disabled={vm.isDeleteLoading}
          sx={{ color: COLORS.error }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

/**
 * Generic System Variable page — BayinerIOT liste deseni.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.icon
 * @param {string} props.title
 * @param {string} props.subtitle
 * @param {string} props.entityLabel          - "Besin", "Tahlil", etc.
 * @param {Object} props.vm                   - return of useSystemVariable
 * @param {Array}  props.tableColumns         - [{ key, label, width?, align?, render? }]
 * @param {Array}  props.formFields           - [{ key, label, required?, multiline?, flex?, type? }]
 * @param {Array}  [props.formSections]       - [{ title, fields: formFields }]  grouped form fields
 * @param {Array}  [props.importColumns]      - columns for import preview table
 * @param {Function} [props.mapExcelRow]      - (row) => item for import
 * @param {Function} [props.downloadTemplate] - () => void
 * @param {Array}  [props.extraFilters]       - ListToolbar searchFields select config: [{ key, type: "select", placeholder, options, value, onChange }]
 */
export default function SystemVariablePage({
  icon, title, subtitle, entityLabel, vm,
  tableColumns, formFields, formSections,
  importColumns, mapExcelRow, downloadTemplate,
  extraFilters,
}) {
  const { palette } = useClinicTheme();
  const bodyRef = useRef(null);
  const autoPageSize = useAutoPageSize(bodyRef, { rowHeight: 56, headerHeight: 40 });

  // Ölçülen satır sayısını yukarı bildir — sayfalama matematiği useSystemVariable'da.
  useEffect(() => {
    vm.setPageSize(autoPageSize);
  }, [autoPageSize, vm.setPageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasImport = Boolean(importColumns && mapExcelRow && downloadTemplate);

  const onPickFile = async (file) => {
    if (!file) return;
    vm.setImportFileName(file.name);
    try {
      const json = await readExcelFile(file);
      const mapped = json.map(mapExcelRow).filter((it) => it.name || it.code);
      vm.setImportItems(mapped);
      vm.setImportPage(0);
    } catch {
      vm.setImportItems([]);
      vm.setErrorMsg("Dosya okunamadı. Lütfen Excel/CSV formatını kontrol edin.");
    }
  };

  const sections = formSections || [{ title: "Bilgiler", fields: formFields }];

  // Başlıklar literal büyük harfe çevrilir — CSS textTransform Türkçe İ'yi
  // bozar. Çağıran zaten büyük harf verdiyse (örn. "TANI KODU" değil "Tanı
  // Kodu") tek seferde toLocaleUpperCase uygulanır, iki kez uygulanmaz.
  const columns = tableColumns.map((col, idx) => {
    const upperLabel = String(col.label ?? "").toLocaleUpperCase("tr-TR");
    const cellSx = col.width ? { width: col.width } : undefined;

    if (idx === 0) {
      return {
        key: col.key,
        label: upperLabel,
        align: col.align,
        cellSx,
        render: (item) => (
          <Box
            component="button"
            type="button"
            onClick={() => vm.openEdit(item)}
            aria-label={`${item?.[col.key] || entityLabel} düzenle`}
            sx={{
              display: "inline-block",
              maxWidth: "100%",
              bgcolor: "transparent",
              border: "none",
              padding: 0,
              margin: 0,
              cursor: "pointer",
              textAlign: "left",
              font: "inherit",
              color: "inherit",
              "&:hover": { textDecoration: "underline", color: palette.primary },
              "&:focus-visible": {
                outline: `2px solid ${alpha(palette.primary, 0.5)}`,
                outlineOffset: 2,
              },
            }}
          >
            {col.render ? col.render(item) : item?.[col.key] ?? "-"}
          </Box>
        ),
      };
    }

    return {
      key: col.key,
      label: upperLabel,
      align: col.align,
      cellSx,
      render: col.render ? (item) => col.render(item) : undefined,
    };
  });

  columns.push({
    key: "__actions",
    label: "",
    align: "center",
    cellSx: { width: 96 },
    render: (item) => (
      <Box sx={{ display: "inline-flex", gap: 0.5 }}>
        <IconButton
          size="small"
          aria-label={`${entityLabel} düzenle`}
          onClick={(e) => { e.stopPropagation(); vm.openEdit(item); }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={`${entityLabel} sil`}
          onClick={(e) => { e.stopPropagation(); vm.openDeleteConfirm(item); }}
          disabled={vm.isDeleteLoading}
          sx={{ color: COLORS.error }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    ),
  });

  const actionItems = [
    { label: `Yeni ${entityLabel}`, icon: <AddIcon sx={{ fontSize: 18 }} />, onClick: vm.openCreate, variant: "primary" },
    ...(hasImport
      ? [
          { label: "Excel Yükle", icon: <CloudUploadIcon sx={{ fontSize: 18 }} />, onClick: () => vm.setImportOpen(true) },
          { label: "Şablon İndir", icon: <DownloadIcon sx={{ fontSize: 18 }} />, onClick: downloadTemplate },
        ]
      : []),
  ];

  return (
    <ListPageShell>
      <ListToolbar
        icon={icon}
        title={title}
        subtitle={subtitle}
        count={vm.total}
        countLabel={entityLabel.toLowerCase()}
        isLoading={vm.isListLoading}
        actionItems={actionItems}
        // Aktif/Pasif sekmesi YOK: pasifleştirme aksiyonu kaldırıldı (yerine
        // silme geldi), her kayıt aktif — sekmeler ölü filtre olurdu.
        searchFields={[
          {
            key: "query",
            placeholder: `${entityLabel} ara`,
            value: vm.filters.query,
            onChange: (v) => vm.setFilter("query", v),
            isPending: vm.pending.query,
          },
          ...(extraFilters || []),
        ]}
        onClear={vm.clearFilters}
      />

      {vm.errorMsg ? <Alert severity="error" sx={{ borderRadius: 0 }}>{vm.errorMsg}</Alert> : null}

      <ListView
        bodyRef={bodyRef}
        columns={columns}
        data={vm.items}
        rowKey="_id"
        renderMobileCard={(item) => (
          <SystemVariableCard item={item} tableColumns={tableColumns} entityLabel={entityLabel} vm={vm} />
        )}
        isLoading={vm.isListLoading}
        loadingNode={
          <ListSkeleton columns={columns} rowCount={vm.pageSize} message={`${title} yükleniyor...`} />
        }
        empty={
          <EmptyState
            icon={icon}
            title="Sonuç bulunamadı"
            description={`Arama/filtre kriterlerinize uygun ${entityLabel.toLowerCase()} bulunamadı.`}
          />
        }
        footer={
          <ListPagination
            currentPage={vm.page}
            totalPages={vm.totalPages}
            totalItems={vm.total}
            itemsPerPage={vm.pageSize}
            onPageChange={vm.setPage}
          />
        }
      />

      {/* CREATE DRAWER */}
      <Drawer anchor="right" open={vm.createOpen} onClose={() => vm.setCreateOpen(false)} PaperProps={{ sx: { width: { xs: "100%", sm: 520 }, maxWidth: "100vw" } }}>
        <FormDrawerContent
          icon={icon} title={`${entityLabel} Ekle`} subtitle={`Yeni ${entityLabel.toLowerCase()} şablonu oluştur`}
          sections={sections} form={vm.form} setField={vm.setField}
          onClose={() => vm.setCreateOpen(false)} onSubmit={vm.submitCreate}
          canSubmit={vm.canSubmit} isLoading={vm.isCreateLoading}
        />
      </Drawer>

      {/* EDIT DIALOG */}
      <FormDialog
        open={vm.editOpen}
        onClose={() => { vm.setEditOpen(false); vm.setEditingItem(null); }}
        icon={icon}
        title={`${entityLabel} Düzenle`}
        maxWidth="sm"
        loading={vm.isUpdateLoading}
        disableCloseWhileLoading={false}
        onSubmit={vm.submitUpdate}
        submitDisabled={!vm.canSubmit}
        submitLabel="Kaydet"
        loadingLabel="Kaydediliyor..."
        cancelLabel="Kapat"
        bodySx={{ gap: 2 }}
      >
        {formFields.map((f) => (
          // Opt-in: `render` verilen alan (ör. anahtar/çoklu seçim) düzenlemede de aynı bileşenle çizilir
          f.render ? (
            <DrawerField key={f.key} label={f.label} fullWidth>{f.render(vm.form, vm.setField)}</DrawerField>
          ) : (
            <FormTextField key={f.key} palette={palette} label={f.label} value={vm.form[f.key] ?? ""} onChange={(e) => vm.setField(f.key, e.target.value)}
              required={f.required} fullWidth multiline={f.multiline} minRows={f.multiline ? 2 : undefined} />
          )
        ))}
      </FormDialog>

      {/* IMPORT DRAWER */}
      {hasImport && (
        <Drawer anchor="right" open={vm.importOpen} onClose={vm.closeImport} PaperProps={{ sx: { width: { xs: "100%", sm: 640 }, maxWidth: "100vw" } }}>
          <ImportDrawerContent
            palette={palette} entityLabel={entityLabel}
            vm={vm} importColumns={importColumns}
            onPickFile={onPickFile} downloadTemplate={downloadTemplate}
          />
        </Drawer>
      )}

      {/* DELETE CONFIRM */}
      <ConfirmDialog
        open={vm.deleteConfirm.open}
        onClose={vm.closeDeleteConfirm}
        onConfirm={vm.confirmDelete}
        title={`${entityLabel} silinsin mi?`}
        message={
          vm.deleteConfirm.item
            ? `"${vm.deleteConfirm.item.name || entityLabel}" listeden kaldırılacak. Bu kaydı kullanan geçmiş hasta kayıtları etkilenmez.`
            : ""
        }
        confirmText="Sil"
        cancelText="İptal"
        confirmVariant="error"
        loading={vm.isDeleteLoading}
      />
    </ListPageShell>
  );
}

/**
 * Form alanları "Personel Oluştur" drawer'ıyla aynı dili konuşur: her alan bir
 * `InfoTile` (ikon + uppercase etiket) içinde `variant="standard"` input olarak
 * çizilir. Outlined + yüzen etiketli TextField KULLANILMAZ — sistem
 * değişkenleri drawer'ları bu yüzden diğer drawer'lardan farklı görünüyordu.
 *
 * `flex` verilmiş alanlar grid'de tek sütun, verilmeyenler tam genişlik kaplar
 * (eski `fullWidth={!f.flex}` davranışının grid karşılığı).
 */
function FormDrawerContent({ icon, title, subtitle, sections, form, setField, onClose, onSubmit, canSubmit, isLoading }) {
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DrawerHeader icon={icon} title={title} subtitle={subtitle} onClose={onClose} />
      <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", flexDirection: "column", gap: 3, flex: 1, overflow: "auto", background: COLORS.white }}>
        {sections.map((sec, si) => (
          <Box key={si}>
            <Typography variant="caption" sx={{ color: COLORS.slate500, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>{sec.title}</Typography>
            <Box sx={{ display: "grid", rowGap: 2.5, columnGap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, mt: 1.5 }}>
              {(sec.fields || []).map((f) => (
                <FormFieldTile key={f.key} field={f} form={form} setField={setField} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      <DrawerFooter
        onCancel={onClose}
        onSubmit={onSubmit}
        submitLabel="Kaydet"
        loadingLabel="Kaydediliyor..."
        loading={isLoading}
        disabled={!canSubmit}
        fullWidth
      />
    </Box>
  );
}

function FormFieldTile({ field: f, form, setField }) {
  return (
    <DrawerField
      label={f.required ? `${f.label} *` : f.label}
      fullWidth={!f.flex}
      sx={f.multiline ? { minHeight: 104 } : undefined}
    >
      {f.render ? (
        f.render(form, setField)
      ) : (
        <FormTextField
          value={form[f.key] ?? ""}
          onChange={(e) => setField(f.key, e.target.value)}
          required={f.required}
          fullWidth
          size="small"
          variant="standard"
          multiline={f.multiline}
          minRows={f.multiline ? 2 : undefined}
          inputProps={{ "aria-label": f.label }}
          InputProps={tileInputProps(true)}
        />
      )}
    </DrawerField>
  );
}

function ImportDrawerContent({ palette, entityLabel, vm, importColumns, onPickFile, downloadTemplate }) {
  const pagedItems = vm.importItems.slice(vm.importPage * vm.importRowsPerPage, vm.importPage * vm.importRowsPerPage + vm.importRowsPerPage);
  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <DrawerHeader
        icon={<UploadFileIcon fontSize="small" />}
        title="Excel/CSV İçe Aktar"
        subtitle="Dosyanı yükle, önizle ve toplu kaydet"
        onClose={vm.closeImport}
      />
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5, flex: 1, overflow: "auto", background: COLORS.slate50 }}>
        <Box sx={{ p: 2.5, bgcolor: "white", borderRadius: 3, border: `1px solid ${COLORS.slate200}` }}>
          <Typography variant="caption" sx={{ color: COLORS.slate500, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" }}>Dosya Yükleme</Typography>
          <Alert severity="info" sx={{ borderRadius: 2, mt: 1.5 }}>Excel dosyanızda ilk satır başlık olmalı. Şablonu indirip doldurman en güvenlisi.</Alert>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 2 }}>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate} sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}>Şablon İndir</Button>
            <Button component="label" variant="contained" startIcon={<CloudUploadIcon />}
              sx={{ background: palette.primary, color: palette.contrastText, "&:hover": { background: palette.primaryDark }, borderRadius: 2, fontWeight: 900, textTransform: "none" }}>
              Dosya Seç
              <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={(e) => onPickFile(e.target.files?.[0])} />
            </Button>
            {vm.importFileName ? <Chip label={vm.importFileName} variant="outlined" sx={{ fontWeight: 800 }} /> : null}
            {vm.importItems.length ? <Chip label={`Toplam: ${vm.importItems.length} kayıt`} color="success" variant="outlined" sx={{ fontWeight: 800 }} /> : null}
          </Box>
        </Box>

        <Box sx={{ p: 2.5, bgcolor: "white", borderRadius: 3, border: `1px solid ${COLORS.slate200}` }}>
          <Typography sx={{ fontWeight: 900, fontSize: TYPO.sectionTitle }}>Önizleme</Typography>
          <TableContainer sx={{ maxHeight: 420, border: `1px solid ${COLORS.slate200}`, borderRadius: 2, mt: 1.5 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {importColumns.map((c) => <TableCell key={c.key} sx={{ fontWeight: 900 }}>{c.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedItems.length ? pagedItems.map((it, i) => (
                  <TableRow key={i} hover>
                    {importColumns.map((c) => <TableCell key={c.key} sx={c.bold ? { fontWeight: 800 } : {}}>{c.render ? c.render(it) : (it[c.key] ?? "-")}</TableCell>)}
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={importColumns.length}><EmptyState variant="compact" icon={<UploadFileIcon />} title="Dosya seçince burada kayıtları göreceksiniz." /></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={vm.importItems.length} page={vm.importPage}
            onPageChange={(_, p) => vm.setImportPage(p)} rowsPerPage={vm.importRowsPerPage}
            onRowsPerPageChange={(e) => { vm.setImportRowsPerPage(parseInt(e.target.value, 10)); vm.setImportPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Sayfa başı" sx={{ mt: 1 }} />
        </Box>
      </Box>
      <DrawerFooter
        onCancel={vm.closeImport}
        onSubmit={vm.submitImport}
        cancelLabel="Kapat"
        submitLabel="İçe Aktar"
        loadingLabel="İçe Aktarılıyor..."
        loading={vm.isBulkLoading}
        disabled={!vm.importItems.length}
        startIcon={<CloudUploadIcon sx={{ fontSize: 15 }} />}
        fullWidth
      />
    </Box>
  );
}
